'use client';

import { useState, useRef, useCallback } from 'react';
import { Upload, X, FileSpreadsheet } from 'lucide-react';
import { useEscapeKey } from '@/hooks/useEscapeKey';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onFileSelect: (file: File) => void;
  accepting?: string;
  maxSizeMB?: number;
}

export function UploadModal({
  open,
  onClose,
  onFileSelect,
  accepting = '.csv,.xlsx,.xls',
  maxSizeMB = 10,
}: UploadModalProps) {
  useEscapeKey(onClose, open);

  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const validateAndSelect = useCallback((file: File) => {
    setError('');

    // Check file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File is too large. Maximum size is ${maxSizeMB} MB.`);
      return;
    }

    // Check extension
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const validExts = accepting.split(',').map((e) => e.trim().replace('.', ''));
    if (!validExts.includes(ext)) {
      setError(`Invalid file type. Accepted: ${validExts.join(', ').toUpperCase()}`);
      return;
    }

    onFileSelect(file);
    onClose();
  }, [accepting, maxSizeMB, onFileSelect, onClose]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSelect(file);
  }, [validateAndSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSelect(file);
    e.target.value = '';
  }, [validateAndSelect]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-base-100/95" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-base-100 border border-base-300/30 rounded-2xl p-6 w-full max-w-lg mx-4 space-y-5 animate-fade-in shadow-2xl">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 btn btn-ghost btn-xs btn-circle">
          <X size={14} />
        </button>

        <div>
          <h2 className="text-lg font-bold">Upload Leads</h2>
          <p className="text-sm text-base-content/40 mt-1">
            Import your leads from a spreadsheet file.
          </p>
        </div>

        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all ${
            dragging
              ? 'border-primary bg-primary/5'
              : 'border-base-300/50 hover:border-primary/30'
          }`}
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
            dragging ? 'bg-primary/20' : 'bg-primary/10'
          }`}>
            {dragging ? (
              <FileSpreadsheet size={22} className="text-primary" />
            ) : (
              <Upload size={22} className="text-primary" />
            )}
          </div>
          <div className="text-center">
            <p className="text-sm font-medium">
              {dragging ? 'Drop your file here' : 'Drag & drop your file here'}
            </p>
            <p className="text-xs text-base-content/40 mt-1">
              or <span className="text-primary underline">click to browse</span>
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={accepting}
            className="hidden"
            onChange={handleFileInput}
          />
        </div>

        {/* Error */}
        {error && (
          <p className="text-xs text-error">{error}</p>
        )}

        {/* Requirements */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-base-content/50">Requirements</p>
          <ul className="text-xs text-base-content/40 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-base-content/20 mt-0.5">•</span>
              <span>Accepted formats: <span className="font-medium text-base-content/50">CSV, XLSX, XLS</span></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-base-content/20 mt-0.5">•</span>
              <span>Maximum file size: <span className="font-medium text-base-content/50">{maxSizeMB} MB</span></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-base-content/20 mt-0.5">•</span>
              <span>First row must be column headers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-base-content/20 mt-0.5">•</span>
              <span>Must contain an email column (we'll detect it automatically)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-base-content/20 mt-0.5">•</span>
              <span>No empty rows between data — rows after gaps may be skipped</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
